import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useState, useEffect } from 'react';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import type { MessageContextWithFeatureId } from '@post-office/browser--townsquare/common/types';

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export type AppProps = {
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
};

export type PlacementRenderProps = {
	dismiss: VoidFunction;
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
} & EligibleMessage;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'goals-app-changeboarding',
	options: {
		choreographer: {
			isDefaultEnabled: true, // You almost always will want this option to be true. See below for an explanation
		},
		hideInIsolatedCloud: false, // Set to true if your placement should not be shown in IC
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => (appProps) => {
	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<Record<string, never>>>;
	}>('/');
	const [messages, setMessages] = useState<EligibleMessage<Record<string, never>>[]>([]);

	useEffect(() => {
		if (isLoading || error) {
			return;
		}
		setMessages(data.messages);
	}, [data, error, isLoading]);

	if (error || isLoading || messages.length === 0) {
		return null;
	}
	const { featureId, children } = appProps;

	const handleDismiss = (messageInstanceId: string): void => {
		setMessages((prevMessages) => {
			const filteredMessages = prevMessages.filter(
				(message) => messageInstanceId !== message.messageInstanceId,
			);
			return filteredMessages;
		});
	};

	const messageToRender = messages.find((message) => {
		const goalsMessage = message as MessageContextWithFeatureId;
		return goalsMessage.featureId === featureId;
	});

	const childrenFunction = children || (() => null);
	return (
		<>
			{messageToRender ? (
				<Messages.render
					key={messageToRender.messageInstanceId}
					dismiss={() => handleDismiss(messageToRender.messageInstanceId)}
					{...messageToRender}
				>
					{childrenFunction}
				</Messages.render>
			) : null}
		</>
	);
});
